import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
// Since we want to discourage direct usages of the usePart* hooks, let's just go ahead
// and disable the no direct redux use selector rule for this file for now.
// eslint-disable-next-line wandb/no-direct-redux-use-selector
import {useDispatch, useSelector} from 'react-redux';

import type {
  DispatchableAction,
  RootAction,
  ThunkResult,
} from '../../types/redux';
import {useMemoizedSelector} from '../hooks';
import * as Actions from './actions';
import * as Selectors from './selectors';
import * as Thunks from './thunks';
import type * as Types from './types';

// Hook that simplifies dispatching redux view part actions, by binding them
// to a specific view part reference.
export function useViewAction<
  RefType extends Types.AllPartRefs,
  ArgType extends any[],
>(ref: RefType, fn: (ref: RefType, ...fnArgs: ArgType) => DispatchableAction) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => dispatch(fn(ref, ...args)),
    [dispatch, ref, fn]
  );
}

export function useMaybeViewAction<
  RefType extends Types.AllPartRefs,
  ArgType extends any[],
>(
  ref: RefType | null | undefined,
  fn: (ref: RefType, ...fnArgs: ArgType) => DispatchableAction
) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => (ref != null ? dispatch(fn(ref, ...args)) : void 0),
    [dispatch, ref, fn]
  );
}

// Like the above version, but binds all arguments. Useful when an action requires multiple refs.
export function useViewActionBindAll<
  RefType extends Types.AllPartRefs,
  ArgType extends any[],
>(
  fn: (ref: RefType, ...fnArgs: ArgType) => RootAction,
  ref: RefType,
  ...args: ArgType
) {
  const dispatch = useDispatch();
  return useCallback(() => {
    return dispatch(fn(ref, ...args));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, ref, fn, ...args]);
}

// Hook that simplifies dispatching redux view actions on a nullable view ref.
export function useViewRefAction<
  RefType extends Types.ViewRef,
  ArgType extends any[],
>(ref: RefType | null, fn: (ref: RefType, ...fnArgs: ArgType) => RootAction) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => (ref != null ? dispatch(fn(ref, ...args)) : void 0),
    [dispatch, ref, fn]
  );
}

export function useViewRefThunk<
  RefType extends Types.ViewRef,
  ArgType extends any[],
  ThunkType extends ThunkResult<any>,
>(ref: RefType | null, fn: (ref: RefType, ...fnArgs: ArgType) => ThunkType) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => (ref != null ? dispatch(fn(ref, ...args)) : void 0),
    [dispatch, ref, fn]
  );
}

export function useObjectCopy<RefType extends Types.AllPartRefs>(ref: RefType) {
  const [copyRef, setCopyRef] = useState<RefType | null>(null);

  const dispatch = useDispatch();
  useEffect(() => {
    // Copy the part on mount
    const action = Actions.copyObject(ref);
    const newRef = action.payload.ref as RefType;
    let copyDispatched = false;
    // Copying a panel still writes into the large panel parts object, which can
    // invalidate expensive panel selectors. Defer it so PanelEditor can paint
    // its loader first; keep the timer cancellable so unmounted editors do not
    // create orphaned copies.
    const copyTimer = setTimeout(() => {
      dispatch(action);
      copyDispatched = true;
      setCopyRef(newRef);
    }, 1);
    return () => {
      // Clean up on unmount
      clearTimeout(copyTimer);
      setCopyRef(null);
      if (copyDispatched) {
        dispatch(Thunks.deleteObject(newRef));
      }
    };
  }, [dispatch, ref]);

  // Becomes true once the copy action succeeds
  const partExists = useMemoizedSelector(
    Selectors.makePartExistsSelector,
    copyRef
  );

  if (copyRef != null && partExists) {
    return {
      // eslint-disable-next-line @typescript-eslint/prefer-as-const
      copying: false as false,
      ref: copyRef,
    };
  }
  // eslint-disable-next-line @typescript-eslint/prefer-as-const
  return {copying: true as true};
}

// This could be more explicitly called useTemporaryWhole but that's
// verbose and ugly. So trying out useNewObjectRef to see how it feels.
export function useNewObjectRef<T extends Types.ObjType>(
  type: T,
  whole: Types.WholeFromType<T>
) {
  return useExistingOrNewObjectRef(type, whole);
}

export function useExistingOrNewObjectRef<T extends Types.ObjType>(
  type: T,
  whole: Types.WholeFromType<T>,
  existing?: Types.PartRefFromType<T>
) {
  const [ref, setRef] = useState<Types.PartRefFromType<T> | null>(null);

  const dispatch = useDispatch();
  useEffect(() => {
    if (existing != null) {
      return;
    }
    const wholeAndType = {
      type,
      whole,
    } as unknown as Types.AllWholeWithTypesHelper<T>;
    // Add the object on mount
    const action = Actions.addObject(wholeAndType);
    dispatch(action);
    const newRef = action.payload.ref as Types.PartRefFromType<T>;
    setRef(newRef);

    // Clean up on unmount
    return () => {
      dispatch(Thunks.deleteObject(newRef));
      setRef(null);
    };
  }, [dispatch, type, whole, existing]);

  // Becomes true once the copy action succeeds
  const partExists = useMemoizedSelector(Selectors.makePartExistsSelector, ref);

  if (existing != null) {
    return {
      // eslint-disable-next-line @typescript-eslint/prefer-as-const
      ready: true as true,
      ref: existing,
    };
  }
  if (ref != null && partExists) {
    return {
      // eslint-disable-next-line @typescript-eslint/prefer-as-const
      ready: true as true,
      ref,
    };
  }
  // eslint-disable-next-line @typescript-eslint/prefer-as-const
  return {ready: false as false};
}

/**
 * Subscribe to an entire normalized part object. Re-renders whenever ANY property of the part changes.
 *
 * @deprecated Prefer reselect selectors with useSelector for better performance.
 * Please read through this comment for more details. Do not add new usages unless absolutely necessary.
 *
 * ## Why deprecated
 *
 * This deprecation is to raise awareness and prevent further usage. `usePart` subscribes to the
 * entire normalized object. When ANY property changes, ALL consumers re-render—even if they only
 * use one property. This can cause expensive cascading re-renders, especially in deeply nested
 * component trees or context providers.
 *
 * ## Still acceptable for
 *
 * - **Accessing nested refs**: When you need refs from the part to pass to other hooks
 *   ```ts
 *   const runSet = usePart(runSetRef);
 *   const filters = usePart(runSet.filtersRef);  // Need the ref from runSet
 *   ```
 * - **Denormalizing**: When reconstructing whole objects from normalized parts
 * - **Need most/all properties**: When you genuinely use most of the part's data
 *
 * ## Better alternative
 *
 * Use reselect selectors with useMemoizedSelector (custom hook) or useSelector (from react-redux):
 *
 * ```ts
 * // 1. Define any necessary selector factories
 * export const makeRunSetSelector = (runSetRef: RunSetRef) =>
 *   (state: RootState) => state.views.parts[runSetRef.type][runSetRef.id];
 *
 * export const makeRunSetPinnedRunIdsSelector = (runSetRef: RunSetRef) =>
 *   createSelector(makeRunSetSelector(runSetRef), runSet => runSet.pinnedRunIds ?? []);
 *
 * // 2a. For parameterized selectors, use useMemoizedSelector
 * const pinnedRunIds = useMemoizedSelector(makeRunSetPinnedRunIdsSelector, runSetRef);
 * // ^ useMemoizedSelector memoizes the selector factory based on args.
 * //   useSelector(makeRunSetPinnedRunIdsSelector(runSetRef)) would create a new
 * //   selector on every render, defeating reselect's memoization.
 *
 * // 2b. For static selectors (no parameters), use useSelector directly
 * const selectCurrentUser = (state: RootState) => state.auth.currentUser;
 * const currentUser = useSelector(selectCurrentUser);
 * // ^ No parameters, so useSelector works fine. The selector reference is stable.
 * ```
 *
 * @see components/RunSelector/runSetSelectors.ts for selector factory examples
 * @see components/RunSelector/RunSelectorRunSetContext.tsx for convenience hook examples
 * @see {@link https://www.notion.so/wandbai/Normalizer-Migration-Toward-Lazy-Denormalization-2dfe2f5c7ef380eb8d83ea5fe27c225b | Lazy Denormalization RFC} for future direction
 */
export function usePart<T extends Types.ObjType>(
  ref: Types.PartRefFromType<T>
) {
  const selector = useMemo(() => Selectors.makePartSelector(ref), [ref]);
  // TS5 module identity issue: `makePartSelector` returns a selector typed against
  // `Types.PartFromType<T>` resolved via `import * as Types from './types'`, while
  // `useSelector` resolves the same path as a separate module instance, producing
  // two nominally identical but structurally unrelated types. The cast breaks the
  // false incompatibility; `ReturnType<typeof selector>` preserves the real return type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useSelector(selector as any) as ReturnType<typeof selector>;
}

/**
 * @deprecated Prefer reselect selectors with useSelector.
 * @see usePart for details and acceptable use cases
 */
export function usePartMaybe<T extends Types.ObjType>(
  ref?: Types.PartRefFromType<T> | null
) {
  const selector = useMemo(
    () => (ref != null ? Selectors.makePartSelector(ref) : () => undefined),
    [ref]
  );
  // See comment in `usePart` above — same TS5 module identity issue applies here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useSelector(selector as any) as ReturnType<typeof selector>;
}

/**
 * @deprecated Prefer reselect selectors with useSelector.
 * @see usePart for details and acceptable use cases
 */
export function useParts<T extends Types.ObjType>(
  refs: Array<Types.PartRefFromType<T>>
) {
  const selector = useMemo(() => Selectors.makePartsSelector(refs), [refs]);
  return useSelector(selector);
}

/**
 * @deprecated Prefer reselect selectors with useSelector.
 * @see usePart for details and acceptable use cases
 */
export function useOptionalPart<T extends Types.ObjType>(
  ref: Types.PartRefFromType<T> | undefined
) {
  const selector = useMemo(
    () => Selectors.makeOptionalPartSelector(ref),
    [ref]
  );
  return useSelector(selector);
}

/**
 * @deprecated Prefer reselect selectors with useSelector.
 * @see usePart for details and acceptable use cases
 */
export function usePartMapped<T extends Types.ObjType, R>(
  ref: Types.PartRefFromType<T>,
  map: (part: Types.PartFromType<T>) => R
) {
  const stateRef = useRef({});
  return useSelector(
    // eslint-disable-next-line wandb/prefer-memoized-selector -- deprecated hook with complex state
    Selectors.makePartMappedSelector(
      ref,
      map as (t: any) => R,
      stateRef.current
    )
  );
}
